import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Typography,
  TextField,
  FormControl,
  Paper,
  Alert,
  InputLabel,
  Tab,
  Tabs,
  Select,
  MenuItem,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Key as KeyIcon,
  Phone as PhoneIcon,
  QrCode2 as QrCodeIcon,
  AppShortcut as AppShortcutIcon,
} from '@mui/icons-material';
import {
  doc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  arrayUnion,
  deleteDoc,
  getDoc,
  addDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { passkeyService } from '../../services/passkey';
import { totpService } from '../../services/totp';
import { useAuth } from '../../contexts/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import { deviceInfoService } from '../../services/deviceInfo';
import type { User } from '../../types/auth';

// Define the neonGreen color here since it's not in theme
const neonGreen = '#39FF14';
const darkBg = '#0a0a0a';
const darkerBg = '#050505';

interface FormData {
  name: string;
  email: string;
  authMethod: 'passkey' | 'totp' | 'phone' | '';
  phoneNumber?: string;
  authenticatorCode?: string;
}

export const SignUpForm: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    authMethod: 'passkey', // Set default auth method
  });
  const [otpauthUrl, setOtpauthUrl] = useState<string>('');
  const [totpSecret, setTotpSecret] = useState<string>('');
  const [isAuthenticatorSetup, setIsAuthenticatorSetup] = useState(false);

  const steps = ['Profile Details', 'Security Setup'];

  const handleNext = () => {
    if (activeStep === 1) {
      handleSubmit();
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      // First create the user document in Firestore
      const userRef = doc(db, 'users', formData.email);
      const now = new Date().toISOString();
      
      // Create initial user data
      const userData: User = {
        id: formData.email,
        email: formData.email,
        name: formData.name,
        provider: formData.authMethod as 'passkey' | 'totp' | 'phone',
        createdAt: now,
        lastLoginAt: now,
        authMethods: {
          passkey: {
            credentials: []
          }
        },
        preferences: {
          theme: 'dark',
          notifications: {
            sms: true,
            push: true,
            trades: true,
            performance: true
          }
        }
      };

      // Create the user document first
      await setDoc(userRef, userData);
      console.log('Created user document');

      // Create an empty strategies collection for the user
      const strategyCollectionRef = collection(db, 'strategies');
      const emptyStrategy = {
        userId: formData.email,
        name: 'Example Strategy',
        description: 'This is an example strategy to help you get started.',
        createdAt: now,
        updatedAt: now,
        isActive: false,
        type: 'manual',
        platform: 'all'
      };
      await addDoc(strategyCollectionRef, emptyStrategy);
      console.log('Created empty strategies collection');

      if (formData.authMethod === 'passkey') {
        try {
          console.log('Starting passkey registration...');
          const registeredPasskey = await passkeyService.register({
            email: formData.email
          });
          console.log('Passkey registered successfully:', registeredPasskey);

          const credential = {
            credentialId: registeredPasskey.credentialId,
            createdAt: registeredPasskey.createdAt,
            lastUsedAt: registeredPasskey.lastUsedAt
          };
          
          console.log('Storing credential:', credential);

          // Update the user's passkey credentials
          await updateDoc(userRef, {
            'authMethods.passkey.credentials': arrayUnion(credential),
            'lastLoginAt': now
          });
          console.log('Updated user with passkey credentials');

          // Get the updated user data
          const updatedDoc = await getDoc(userRef);
          const updatedData = updatedDoc.data() as User;
          console.log('Updated user data:', updatedData);

          // Register device information - do this asynchronously
          deviceInfoService.registerDeviceForUser(formData.email, 'passkey')
            .then(() => console.log('Registered device information'))
            .catch(err => console.warn('Failed to register device:', err));

          // Set the user in AuthContext directly - no need for a second authentication
          dispatch({ type: 'SET_USER', payload: updatedData });
          console.log('Set user in AuthContext');

          // Registration successful
          setSuccess(true);
          setError('');
          
          // Show success message
          alert('Account created successfully! Redirecting to dashboard...');
          
          // Navigate to dashboard
          navigate('/dashboard');
          return; // Exit early to prevent the code below from executing
        } catch (error) {
          console.error('Error registering passkey:', error);
          // If passkey registration fails, delete the user document
          await deleteDoc(userRef);
          setError(error instanceof Error ? error.message : 'Failed to register passkey. Please try again.');
          setSuccess(false);
          setLoading(false);
          return; // Exit early to prevent the code below from executing
        }
      }

      // Handle other auth methods (totp, phone)
      // Registration successful
      setSuccess(true);
      setError('');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'Failed to create user');
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAuthMethodSelect = async (method: 'passkey' | 'authenticator' | 'sms') => {
    // Map UI methods to internal methods
    const internalMethod = method === 'authenticator' ? 'totp' : 
                          method === 'sms' ? 'phone' : method;
    
    setFormData(prev => ({ ...prev, authMethod: internalMethod }));
    
    if (method === 'authenticator') {
      if (!formData.email) {
        setError('Please enter your email first');
        return;
      }
      
      try {
        const { otpauthUrl, secret } = await totpService.generateTOTP(formData.email);
        setOtpauthUrl(otpauthUrl);
        setTotpSecret(secret);
      } catch (error) {
        console.error('Error generating TOTP:', error);
        setError('Failed to generate authenticator setup. Please try again.');
      }
    }
  };

  const getDisplayMethod = (internalMethod: string): 'passkey' | 'authenticator' | 'sms' => {
    switch (internalMethod) {
      case 'totp':
        return 'authenticator';
      case 'phone':
        return 'sms';
      case 'passkey':
        return 'passkey';
      default:
        return 'passkey'; // Default to passkey if method is empty or unknown
    }
  };

  const renderAuthMethodSetup = () => {
    switch (formData.authMethod) {
      case 'passkey':
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, color: 'grey.300' }}>
              Click "Complete Sign Up" to register your device as a passkey.
            </Typography>
          </Box>
        );
      
      case 'totp':
        return (
          <Box sx={{ mt: 3 }}>
            {otpauthUrl && (
              <>
                <Box sx={{ mb: 2, textAlign: 'center' }}>
                  <QRCodeSVG
                    value={otpauthUrl}
                    size={200}
                    level="M"
                    includeMargin={true}
                    style={{ background: 'white', padding: '10px', borderRadius: '8px' }}
                  />
                </Box>
                <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                  1. Scan this QR code with your authenticator app
                </Typography>
              </>
            )}
            <TextField
              fullWidth
              label="Enter Code from Authenticator"
              variant="outlined"
              value={formData.authenticatorCode || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, authenticatorCode: e.target.value }))}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'grey.100',
                  '& fieldset': {
                    borderColor: 'grey.700',
                  },
                  '&:hover fieldset': {
                    borderColor: 'grey.500',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: neonGreen,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'grey.500',
                  '&.Mui-focused': {
                    color: neonGreen,
                  },
                },
              }}
            />
          </Box>
        );
      
      case 'phone':
        return (
          <Box sx={{ mt: 3 }}>
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={formData.phoneNumber || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
              placeholder="+1 (555) 555-5555"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  color: 'grey.100',
                  '& fieldset': {
                    borderColor: 'grey.700',
                  },
                  '&:hover fieldset': {
                    borderColor: 'grey.500',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: neonGreen,
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'grey.500',
                  '&.Mui-focused': {
                    color: neonGreen,
                  },
                },
              }}
            />
          </Box>
        );
      
      default:
        return null;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 3,
                color: 'grey.100',
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 500
              }}
            >
              Create Your Profile
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                required
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                variant="outlined"
                error={formData.name.length > 0 && formData.name.length < 2}
                helperText={formData.name.length > 0 && formData.name.length < 2 ? "Name must be at least 2 characters" : ""}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${neonGreen}44`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${neonGreen}88`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: neonGreen,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'grey.500',
                    '&.Mui-focused': {
                      color: neonGreen,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'error.main',
                  }
                }}
              />

              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                variant="outlined"
                error={formData.email.length > 0 && !formData.email.includes('@')}
                helperText={formData.email.length > 0 && !formData.email.includes('@') ? "Please enter a valid email address" : ""}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: `${neonGreen}44`,
                    },
                    '&:hover fieldset': {
                      borderColor: `${neonGreen}88`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: neonGreen,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'grey.500',
                    '&.Mui-focused': {
                      color: neonGreen,
                    },
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'error.main',
                  }
                }}
              />
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!formData.name || !formData.email || !formData.email.includes('@') || formData.name.length < 2}
                sx={{
                  backgroundColor: neonGreen,
                  color: 'black',
                  '&:hover': {
                    backgroundColor: `${neonGreen}cc`,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: `${neonGreen}44`,
                    color: 'grey.700',
                  },
                }}
              >
                Next
              </Button>
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2,
                color: 'grey.100',
                textAlign: 'center',
                fontFamily: 'Inter, sans-serif',
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                fontWeight: 500
              }}
            >
              Choose Your Authentication Method
            </Typography>

            <Box sx={{ 
              borderBottom: 1, 
              borderColor: 'divider', 
              mb: 3, 
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <Tabs
                value={getDisplayMethod(formData.authMethod)}
                onChange={(_, value) => handleAuthMethodSelect(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  '& .MuiTab-root': {
                    minWidth: { xs: 'auto', sm: 100 },
                    color: 'grey.500',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    padding: { xs: '6px 8px', sm: '6px 16px' },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      mb: { xs: 0.5, sm: 1 },
                    },
                    '&.Mui-selected': {
                      color: neonGreen,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: neonGreen,
                  },
                }}
              >
                <Tab
                  icon={<KeyIcon />}
                  label="Passkey"
                  value="passkey"
                />
                <Tab
                  icon={<QrCodeIcon />}
                  label="Authenticator App"
                  value="authenticator"
                />
                <Tab
                  icon={<PhoneIcon />}
                  label="SMS"
                  value="sms"
                />
              </Tabs>
            </Box>

            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderAuthMethodSetup()}
            </Box>

            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                onClick={handleBack}
                sx={{
                  backgroundColor: 'grey.800',
                  color: 'grey.100',
                  '&:hover': {
                    backgroundColor: 'grey.700',
                  },
                }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!formData.authMethod || 
                  (formData.authMethod === 'phone' && !formData.phoneNumber) ||
                  (formData.authMethod === 'totp' && !formData.authenticatorCode)}
                sx={{
                  backgroundColor: neonGreen,
                  color: 'black',
                  '&:hover': {
                    backgroundColor: `${neonGreen}cc`,
                  },
                  '&.Mui-disabled': {
                    backgroundColor: `${neonGreen}44`,
                    color: 'grey.700',
                  },
                }}
              >
                Complete Sign Up
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  const renderButtons = () => null;

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: { xs: '100%', sm: 500 },
      minWidth: { xs: '100%', sm: 400 },
      px: { xs: 2, sm: 0 }
    }}>
      <Paper 
        elevation={3}
        sx={{
          p: { xs: 2, sm: 4 },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${neonGreen}44`
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          align="center" 
          sx={{ 
            color: neonGreen,
            fontFamily: 'Orbitron, sans-serif',
            fontSize: { xs: '2rem', sm: '2.5rem' },
            fontWeight: 600,
            textShadow: `0 0 10px ${neonGreen}88`,
            letterSpacing: '0.05em',
            textTransform: 'uppercase'
          }}
        >
          Sign Up
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress sx={{ color: neonGreen }} />
          </Box>
        )}

        <Stepper 
          activeStep={activeStep} 
          sx={{ 
            mb: 4,
            '& .MuiStepLabel-label': {
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: '0.875rem', sm: '1rem' },
              color: 'grey.500',
              '&.Mui-active': {
                color: neonGreen,
              },
              '&.Mui-completed': {
                color: `${neonGreen}cc`,
              },
            },
            '& .MuiStepIcon-root': {
              color: 'grey.700',
              '&.Mui-active': {
                color: neonGreen,
              },
              '&.Mui-completed': {
                color: `${neonGreen}cc`,
              },
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        {renderButtons()}
      </Paper>
    </Box>
  );
};
